import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";

export default function AllTotal() {
  const today = new Date().toISOString().split("T")[0];

  // Fetch data using RTK Query
  const { data: salesData, isLoading: salesLoading } = useGetSalesQuery();
  const { data: expensesData, isLoading: expensesLoading } = useGetExpensesQuery({
    startDate: "1900-01-01", // Placeholder for earliest date
    endDate: today,
  });
  const { data: incomesData, isLoading: incomesLoading } = useGetIncomesQuery({
    startDate: "1900-01-01", // Placeholder for earliest date
    endDate: today,
  });
  const { data: depositsData, isLoading: depositsLoading } = useGetDepositsQuery({
    startDate: "1900-01-01", // Placeholder for earliest date
    endDate: today,
  });
  const { data: suppliesData, isLoading: suppliesLoading } = useGetSuppliesQuery();

  const [totals, setTotals] = useState({
    totalSales: 0,
    totalExpenses: 0,
    totalIncomes: 0,
    totalDeposits: 0,
    totalPurchases: 0,
    netCash: 0,
    difference: 0,
    cashInHand: 0,
  });

  useEffect(() => {
    if (
      !salesLoading &&
      !expensesLoading &&
      !incomesLoading &&
      !depositsLoading &&
      !suppliesLoading
    ) {
      // Calculate general totals
      const totalSales = salesData?.sales?.reduce((sum, sale) => sum + Number(sale.totalPrice || 0), 0) || 0;

      const totalExpenses = expensesData?.expenses?.reduce(
        (sum, expense) => sum + Number(expense.amount || 0),
        0
      ) || 0;

      const totalIncomes = incomesData?.incomes?.reduce(
        (sum, income) => sum + Number(income.amount || 0),
        0
      ) || 0;

      const totalDepositsRaw = depositsData?.deposits?.reduce(
        (sum, deposit) => sum + Number(deposit.amount || 0),
        0
      ) || 0;
      const totalDeposits = totalDepositsRaw > 0 ? totalDepositsRaw * -1 : totalDepositsRaw * +1;

      const totalPurchasesRaw = suppliesData?.supplies?.reduce(
        (sum, supply) => sum + Number(supply.cardCost || 0),
        0
      ) || 0;
      const totalPurchases = totalPurchasesRaw * -1; // Always multiply by -1

      // Calculate NetCash, Difference, and CashInHand
      const netCash = totalSales + totalExpenses;
      const difference = totalIncomes - netCash;
      const cashInHand = netCash + totalDeposits + totalPurchases;

      // Update totals state
      setTotals({
        totalSales: totalSales.toFixed(2),
        totalExpenses: totalExpenses.toFixed(2),
        totalIncomes: totalIncomes.toFixed(2),
        totalDeposits: totalDeposits.toFixed(2),
        totalPurchases: totalPurchases.toFixed(2),
        netCash: netCash.toFixed(2),
        difference: difference.toFixed(2),
        cashInHand: cashInHand.toFixed(2),
      });
    }
  }, [
    salesData,
    expensesData,
    incomesData,
    depositsData,
    suppliesData,
    salesLoading,
    expensesLoading,
    incomesLoading,
    depositsLoading,
    suppliesLoading,
  ]);

  // Export totals to Excel
  const exportToExcel = () => {
    const exportData = [
      { Header: "Total Sales", Value: totals.totalSales },
      { Header: "Total Expenses", Value: totals.totalExpenses },
      { Header: "Total Incomes", Value: totals.totalIncomes },
      { Header: "Total Deposits", Value: totals.totalDeposits },
      { Header: "Total Purchases", Value: totals.totalPurchases },
      { Header: "Net Cash", Value: totals.netCash },
      { Header: "Difference", Value: totals.difference },
      { Header: "Cash In Hand", Value: totals.cashInHand },
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AllTotals");
    XLSX.writeFile(workbook, "AllTotals.xlsx");
  };

  return (
    <div className="all-total">
      <h1>All Collections Total</h1>

      {salesLoading ||
      expensesLoading ||
      incomesLoading ||
      depositsLoading ||
      suppliesLoading ? (
        <div>Loading data...</div>
      ) : (
        <>
          <table className="totals-table">
            <thead>
              <tr>
                <th>Header</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Total Sales</strong></td>
                <td>${totals.totalSales}</td>
              </tr>
              <tr>
                <td><strong>Total Income and Expenses</strong></td>
                <td>${totals.totalExpenses}</td>
              </tr>
              <tr>
                <td><strong>Net Cash </strong></td>
                <td>${totals.netCash}</td>
              </tr>
              <tr>
                <td><strong>Total Cash Collected</strong></td>
                <td>${totals.totalIncomes}</td>
              </tr>
              <tr>
                <td><strong>Difference</strong></td>
                <td>${totals.difference}</td>
              </tr>
              <tr>
                <td><strong>Net Bank</strong></td>
                <td>${totals.totalDeposits}</td>
              </tr>
              <tr>
                <td><strong>Total Purchases</strong></td>
                <td>${totals.totalPurchases}</td>
              </tr>
              <tr>
                <td><strong>Cash In Hand</strong></td>
                <td>${totals.cashInHand}</td>
              </tr>
            </tbody>
          </table>

          <button onClick={exportToExcel} className="export-button">
            Export to Excel
          </button>
        </>
      )}
    </div>
  );
}